import { Suspense, lazy, useContext, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import Styles from "./Home.module.css";
import {
  LanguageProvider,
  HomePageContext,
  HomePageProvider,
  AboutusPageProvider,
} from "../../store";
import { useScrollPosition } from "../../hooks";
import PageViewTracker from "../PageViewTracker";
import Community from "../../Component/Community";

// Lazy load components
const HomeHero = lazy(() => import("../../Component/HomeHero"));
const LearnMaflam = lazy(() => import("../../Component/LearnMaflam"));
const ViewCourses = lazy(() => import("../../Component/ViewCourses"));
const CourseCategories = lazy(() => import("../../Component/CourseCategories"));
const FundamentalPackage = lazy(() =>
  import("../../Component/FundamentalPackage")
);
const Ourservices = lazy(() => import("../../Component/Ourservices"));
const SuccessPartners = lazy(() => import("../../Component/SuccessPartners"));
const LearnAbout = lazy(() => import("../../Component/LearnAbout"));
const MaflamInstructors = lazy(() =>
  import("../../Component/MaflamInstructors")
);
const HomeScreenCarousel = lazy(() =>
  import("../../Component/HomeScreenCarousel")
);
const BlogContainer = lazy(() => import("../../Component/BlogContainer"));
const HomeCommunityCarousel = lazy(() =>
  import("../../Component/HomeCommunityCarousel")
);
const Faqs = lazy(() => import("../../Component/Faqs"));
const JoinUsHomePage = lazy(() =>
  import("../../Component/JoinUs/JoinUsHomePage")
);

const Home = () => {
  const location = useLocation();
  const {
    homeScreenDetails,
    loading: contextLoading,
    error,
  } = useContext(HomePageContext);
  const [isLoading, setIsLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const contentRef = useRef(null);
  const timeoutRef = useRef();
  const direction = "rtl";

  console.log('Home Component Mounted');
  console.log('Initial State:', {
    contextLoading,
    error,
    homeScreenDetails,
    isLoading,
    contentLoaded,
    imagesLoaded
  });

  // Scroll management
  useEffect(() => {
    console.log('Scroll Effect Running');
    window.scrollTo(0, 0);
    const preventScroll = (e) => isLoading && window.scrollTo(0, 0);
    window.addEventListener("scroll", preventScroll);
    return () => window.removeEventListener("scroll", preventScroll);
  }, [location.pathname, isLoading]);

  // Unified loading timeout
  useEffect(() => {
    console.log('Loading Timeout Effect Running');
    timeoutRef.current = setTimeout(() => {
      console.log('Loading Timeout Completed');
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timeoutRef.current);
  }, []);

  // Handle content loading
  useEffect(() => {
    console.log('Content Loading Effect Running', {
      contextLoading,
      hasHomeScreenDetails: !!homeScreenDetails
    });
    
    if (!contextLoading && homeScreenDetails) {
      console.log('Content Loaded Successfully');
      window.scrollTo(0, 0);
      setContentLoaded(true);
    }
  }, [contextLoading, homeScreenDetails]);

  // Image loading handler
  useEffect(() => {
    console.log('Image Loading Effect Running', {
      hasContentRef: !!contentRef.current,
      contentLoaded
    });

    if (!contentRef.current || !contentLoaded) return;

    const images = contentRef.current.getElementsByTagName("img");
    let loaded = 0;
    const total = images.length;

    console.log('Found Images:', { total });

    const handleImageLoad = () => {
      loaded++;
      console.log('Image Loaded:', { loaded, total });
      if (loaded === total) {
        console.log('All Images Loaded');
        setImagesLoaded(true);
      }
    };

    if (total === 0) {
      console.log('No Images Found, Setting Images Loaded');
      return setImagesLoaded(true);
    }

    Array.from(images).forEach((img) => {
      if (img.complete) handleImageLoad();
      else img.addEventListener("load", handleImageLoad);
    });

    return () =>
      Array.from(images).forEach((img) =>
        img.removeEventListener("load", handleImageLoad)
      );
  }, [contentLoaded]);

  // Early loading completion handler
  useEffect(() => {
    console.log('Early Loading Completion Effect Running', {
      contentLoaded,
      imagesLoaded,
      isLoading
    });

    if (contentLoaded && imagesLoaded && isLoading) {
      console.log('Early Loading Completion Triggered');
      clearTimeout(timeoutRef.current);
      setIsLoading(false);
    }
  }, [contentLoaded, imagesLoaded, isLoading]);

  // Popup handling
  useEffect(() => {
    const hasSeenPopup = localStorage.getItem("hasSeenPopup");
    if (!localStorage.getItem("authData") && !hasSeenPopup) {
      setShowPopup(true);
      localStorage.setItem("hasSeenPopup", "true");
    }
  }, []);

  const handleClosePopup = () => setShowPopup(false);

  if (isLoading)
    return (
      <div className={Styles.loaderWrapper}>
        <span className={Styles.loader}></span>
      </div>
    );

  if (error) return <div>Error loading content</div>;

  return (
    <LanguageProvider>
      <HomePageProvider>
        <div className={Styles.Home} ref={contentRef}>
          <AboutusPageProvider>
            {showPopup && (
              <div
                className={`${Styles.popupWrapper} ${
                  direction === "rtl" ? Styles.rtl : Styles.ltr
                }`}
              >
                <button
                  className={Styles.closeButton}
                  onClick={handleClosePopup}
                >
                  X
                </button>
                <Suspense fallback={null}>
                  <JoinUsHomePage />
                </Suspense>
              </div>
            )}
          </AboutusPageProvider>
          <PageViewTracker />
          <div className={Styles.heroWrapper}>
            <HomeHero />
            <div className={Styles.heroGradient}></div>
          </div>

          <Suspense fallback={null}>
            <LearnMaflam />
            <ViewCourses />
            <CourseCategories />
            <FundamentalPackage />
            <div id="ourservices" className={Styles.ourservicescontainer}>
              <Ourservices />
              <SuccessPartners />
            </div>
            <LearnAbout />
                <MaflamInstructors />
            <div className={Styles.BlogContainer}>
              <HomeScreenCarousel />
              <BlogContainer />
              <HomeCommunityCarousel />
            </div>
            <Community />
            <div className={Styles.Footer}>
              <Faqs />
            </div>
          </Suspense>
        </div>
      </HomePageProvider>
    </LanguageProvider>
  );
};

export default Home;
